import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'

interface Country {
  id: string
  name: string
  code: string
  region: string
  population: number
  treasury: number
  gdp: number
  unemployment: number
  inflation: number
  happiness: number
  stability: number
  approval_rating: number
  current_turn: number
  government_type_id: string
}

interface Game {
  id: string
  user_id: string
  difficulty: string
  game_mode: string
  current_turn: number
  active: number
}

interface GameData {
  game: Game & Country
  events: any[]
  diplomaticEvents: any[]
  history: any[]
}

function GameViewPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [gameData, setGameData] = useState<GameData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [advancingTurn, setAdvancingTurn] = useState(false)

  useEffect(() => {
    if (!gameId || !token) {
      navigate('/dashboard')
      return
    }

    fetchGameData()
  }, [gameId, token])

  const fetchGameData = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/games/${gameId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch game data')
      }

      const data = await response.json()
      if (data.success) {
        setGameData(data)
      } else {
        setError(data.error || 'Failed to load game')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`
    if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`
    if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`
    return `$${num.toFixed(0)}`
  }

  const formatPopulation = (num: number) => {
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`
    return num.toString()
  }

  const advanceToNextTurn = async () => {
    try {
      setAdvancingTurn(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/games/${gameId}/next-turn`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      if (data.success) {
        // Refresh game data to show new turn
        await fetchGameData()
      } else {
        setError(data.error || 'Failed to advance turn')
      }
    } catch (err) {
      setError('Error advancing to next turn')
    } finally {
      setAdvancingTurn(false)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading game data...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card bg-red-50 border-red-200">
          <p className="text-red-600">Error: {error}</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="btn btn-secondary mt-4"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!gameData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card">
          <p className="text-gray-600">Game not found</p>
        </div>
      </div>
    )
  }

  const { game } = gameData

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{game.name}</h1>
          <p className="text-gray-600">Turn {game.current_turn} • {game.difficulty} • {game.game_mode}</p>
        </div>
        <button 
          onClick={() => navigate('/dashboard')}
          className="btn btn-secondary"
        >
          Back to Dashboard
        </button>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Treasury</h3>
          <p className="text-2xl font-bold text-green-600">{formatNumber(game.treasury)}</p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">GDP</h3>
          <p className="text-2xl font-bold text-blue-600">{formatNumber(game.gdp)}</p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Population</h3>
          <p className="text-2xl font-bold text-purple-600">{formatPopulation(game.population)}</p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Approval Rating</h3>
          <p className={`text-2xl font-bold ${game.approval_rating >= 60 ? 'text-green-600' : game.approval_rating >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
            {Math.round(game.approval_rating)}%
          </p>
        </div>
      </div>

      {/* Economic Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Unemployment</h3>
          <p className={`text-xl font-semibold ${game.unemployment <= 5 ? 'text-green-600' : game.unemployment <= 8 ? 'text-yellow-600' : 'text-red-600'}`}>
            {game.unemployment.toFixed(1)}%
          </p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Inflation</h3>
          <p className={`text-xl font-semibold ${game.inflation <= 3 ? 'text-green-600' : game.inflation <= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
            {game.inflation.toFixed(1)}%
          </p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Happiness</h3>
          <p className={`text-xl font-semibold ${game.happiness >= 60 ? 'text-green-600' : game.happiness >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
            {Math.round(game.happiness)}%
          </p>
        </div>
        
        <div className="card">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Stability</h3>
          <p className={`text-xl font-semibold ${game.stability >= 70 ? 'text-green-600' : game.stability >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
            {Math.round(game.stability)}%
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <button 
          className="card hover:bg-blue-50 transition-colors p-6 text-center cursor-pointer border-2 border-transparent hover:border-blue-200"
          onClick={() => navigate(`/game/${gameId}/budget`)}
        >
          <div className="text-2xl mb-2">💰</div>
          <h3 className="font-semibold text-gray-900">Manage Budget</h3>
          <p className="text-sm text-gray-600 mt-1">Allocate spending across government sectors</p>
        </button>
        
        <button 
          className="card hover:bg-green-50 transition-colors p-6 text-center cursor-pointer border-2 border-transparent hover:border-green-200"
          onClick={() => navigate(`/game/${gameId}/taxes`)}
        >
          <div className="text-2xl mb-2">🏛️</div>
          <h3 className="font-semibold text-gray-900">Set Tax Rates</h3>
          <p className="text-sm text-gray-600 mt-1">Adjust taxation policies and rates</p>
        </button>
        
        <button 
          className="card hover:bg-purple-50 transition-colors p-6 text-center cursor-pointer border-2 border-transparent hover:border-purple-200"
          onClick={() => navigate(`/game/${gameId}/events`)}
        >
          <div className="text-2xl mb-2">📋</div>
          <h3 className="font-semibold text-gray-900">View Events</h3>
          <p className="text-sm text-gray-600 mt-1">Handle current events and crises</p>
          {(gameData.events?.length > 0 || gameData.diplomaticEvents?.length > 0) && (
            <span className="inline-block bg-red-500 text-white text-xs rounded-full px-2 py-1 mt-2">
              {(gameData.events?.length || 0) + (gameData.diplomaticEvents?.length || 0)} pending
            </span>
          )}
        </button>
      </div>

      {/* Next Turn Section */}
      <div className="card mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Turn Management</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600">
              Complete all required actions to advance to Turn {game.current_turn + 1}
            </p>
            <div className="mt-2 space-x-4 text-sm">
              <span className="text-green-600">✓ Country Status: Active</span>
              {(gameData.events?.length || 0) + (gameData.diplomaticEvents?.length || 0) > 0 ? (
                <span className="text-red-600">⚠ {(gameData.events?.length || 0) + (gameData.diplomaticEvents?.length || 0)} events pending</span>
              ) : (
                <span className="text-green-600">✓ All events resolved</span>
              )}
            </div>
          </div>
          
          <button 
            onClick={() => advanceToNextTurn()}
            disabled={advancingTurn || (gameData.events?.length || 0) + (gameData.diplomaticEvents?.length || 0) > 0}
            className={`px-6 py-3 font-semibold rounded-lg ${
              !advancingTurn && ((gameData.events?.length || 0) + (gameData.diplomaticEvents?.length || 0)) === 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {advancingTurn ? 'Processing...' : 'Next Turn'}
          </button>
        </div>
      </div>

      {/* Recent History */}
      {gameData.history && gameData.history.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Actions</h3>
          <div className="space-y-2">
            {gameData.history.slice(0, 5).map((entry: any, index: number) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-600">Turn {entry.turn}: {entry.action}</span>
                <span className="text-xs text-gray-400">{new Date(entry.timestamp).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default GameViewPage